import { useEffect, useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { SeatGrid } from "../site/SeatGrid";
import { api, apiError } from "../../lib/api";

const TOOLS = [["Semicama", "seat"], ["Coche cama", "seat"], ["Suite", "seat"], ["Escalera", "stairs"], ["Baño", "wc"], ["Borrar", null]];
const BLANK = { name: "", decks: [{ name: "Planta alta", rows: 12, cols: 5, cells: [] }, { name: "Planta baja", rows: 5, cols: 4, cells: [] }] };

export const LayoutEditor = ({ layout, open, onClose, onSaved }) => {
  const [l, setL] = useState(BLANK);
  const [tool, setTool] = useState(TOOLS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setL(layout ? JSON.parse(JSON.stringify(layout)) : JSON.parse(JSON.stringify(BLANK))); }, [layout, open]);

  const paint = (di, r, c) => {
    const decks = [...l.decks];
    const cells = decks[di].cells.filter((x) => !(x.r === r && x.c === c));
    if (tool[1]) cells.push({ r, c, kind: tool[1], category: tool[1] === "seat" ? tool[0] : null, number: null });
    decks[di] = { ...decks[di], cells };
    setL({ ...l, decks });
  };
  const autoNumber = () => {
    let n = 1;
    setL({ ...l, decks: l.decks.map((d) => ({ ...d, cells: [...d.cells].sort((a, b) => a.r - b.r || a.c - b.c).map((x) => (x.kind === "seat" ? { ...x, number: n++ } : x)) })) });
  };
  const setDeck = (i, k, v) => setL({ ...l, decks: l.decks.map((d, j) => (j === i ? { ...d, [k]: v } : d)) });

  const save = async () => {
    const nums = l.decks.flatMap((d) => d.cells.filter((x) => x.kind === "seat").map((x) => x.number));
    if (nums.some((x) => !x)) return toast.error("Numerá las butacas antes de guardar");
    if (new Set(nums).size !== nums.length) return toast.error("Hay números de butaca repetidos");
    setSaving(true);
    try {
      const { id, ...body } = l;
      if (id) await api.put(`/admin/layouts/${id}`, body); else await api.post("/admin/layouts", body);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="layout-editor" className="max-h-[94vh] max-w-4xl overflow-y-auto rounded-3xl [&>*]:min-w-0" data-lenis-prevent>
        <DialogHeader><DialogTitle className="font-display text-3xl">Diagrama de asientos</DialogTitle></DialogHeader>
        <Input data-testid="layout-name" placeholder="Nombre (ej. Doble piso 60)" value={l.name} onChange={(e) => setL({ ...l, name: e.target.value })} className="h-11 rounded-xl" />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={l.decks.length === 2} onChange={(e) => setL({ ...l, decks: e.target.checked ? [...l.decks, BLANK.decks[1]] : l.decks.slice(0, 1) })} className="h-4 w-4 accent-[var(--forest)]" /> Doble piso</label>
          {l.decks.map((d, i) => (
            <span key={i} className="flex items-center gap-1 text-xs">{d.name}: <Input type="number" min={1} max={30} value={d.rows} onChange={(e) => setDeck(i, "rows", +e.target.value || 1)} className="h-8 w-14" /> filas × <Input type="number" min={1} max={8} value={d.cols} onChange={(e) => setDeck(i, "cols", +e.target.value || 1)} className="h-8 w-12" /> col.</span>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {TOOLS.map((t) => <button key={t[0]} type="button" onClick={() => setTool(t)} data-testid={`layout-tool-${t[0]}`} className={`rounded-full px-4 py-2 text-sm font-bold ${tool[0] === t[0] ? "bg-[var(--forest)] text-white" : "bg-[var(--paper)]"}`}>{t[0]}</button>)}
          <button type="button" onClick={autoNumber} data-testid="layout-autonumber" className="inline-flex items-center gap-2 rounded-full bg-[var(--lime)] px-4 py-2 text-sm font-bold"><Wand2 className="h-4 w-4" /> Numerar butacas</button>
        </div>
        <p className="text-xs text-[var(--muted-ink)]">Elegí una herramienta y tocá los casilleros. Los vacíos son pasillo.</p>
        <div className="space-y-4">
          {l.decks.map((d, di) => (
            <div key={di}>
              <div className="mx-auto grid w-max gap-1.5 rounded-2xl bg-[var(--paper)] p-3" style={{ gridTemplateColumns: `repeat(${d.cols}, 2.5rem)` }}>
                {Array.from({ length: d.rows * d.cols }, (_, i) => {
                  const r = Math.floor(i / d.cols), c = i % d.cols;
                  const cell = d.cells.find((x) => x.r === r && x.c === c);
                  return (
                    <button key={i} type="button" onClick={() => paint(di, r, c)} data-testid={`layout-cell-${di}-${r}-${c}`} title={cell?.category || cell?.kind || "pasillo"}
                      className={`h-10 w-10 rounded-lg border text-[10px] font-bold ${!cell ? "border-dashed border-black/15 bg-white/50" : cell.kind !== "seat" ? "bg-black/20" : cell.category === "Semicama" ? "border-[var(--forest)] bg-white text-[var(--forest)]" : cell.category === "Suite" ? "bg-[var(--ink)] text-white" : "bg-[var(--forest)] text-white"}`}>
                      {cell?.kind === "seat" ? cell.number ?? "·" : cell?.kind === "wc" ? "WC" : cell?.kind === "stairs" ? "≡" : ""}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-center font-mono text-[11px] text-[var(--muted-ink)]">{d.name} · {d.cells.filter((x) => x.kind === "seat").length} butacas</p>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="layout-save" className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar diagrama</button>
      </DialogContent>
    </Dialog>
  );
};

export const LayoutsPanel = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/admin/layouts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="layouts-panel">
      <button onClick={() => setEdit(null)} data-testid="layouts-add" className="rounded-full bg-[var(--ink)] px-5 py-3 text-sm font-bold text-white">+ Nuevo diagrama</button>
      <div className="mt-5 grid gap-4">
        {items.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display-up text-lg">{l.name}</p>
              <div className="flex gap-2">
                <button onClick={() => setEdit(l)} data-testid={`layout-edit-${l.id}`} className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold">Editar</button>
                <button onClick={async () => { if (window.confirm("¿Eliminar diagrama?")) { await api.delete(`/admin/layouts/${l.id}`); load(); } }} className="rounded-full border border-black/10 px-3 py-2 text-sm text-red-700">Eliminar</button>
              </div>
            </div>
            <SeatGrid decks={l.decks} stateOf={() => "free"} testPrefix={`lay-${l.id}`} />
          </div>
        ))}
      </div>
      <LayoutEditor open={edit !== undefined} layout={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
};
